import { defineStore } from 'pinia'
import { computed, nextTick, ref, shallowRef } from 'vue'
import type {
  HabilitationsAdminMode,
  HabilitationsAdminProps,
  HabilitationsAdminPropsModeAdmin, HabilitationsAdminPropsModeUser,
  TokenData,
} from './HabilitationsAdmin.type'
import { useImmerFieldPatcher } from '@/features/interface/composables/ImmerFieldPatcher'
import { buildRattachementAdminService } from '@/features/services/profil/RattachementAdminService'
import type { ContactDTO, EntiteDTO } from '@/features/api/habilitation/dto'
import { deepEqual, isNotNullUndefinedOrEmptyString } from '@/features/shared/tools/object'
import { b64DecodeUnicode, urlSafeToStandard } from '@/features/shared/tools/b64'

type EngineStateAdmin = Partial<HabilitationsAdminPropsModeAdmin> & { isRunning: boolean, identifiantModel?: ContactDTO | Entite }
type EngineStateUser = Partial<HabilitationsAdminPropsModeUser> & { isRunning: boolean }
type EngineState = EngineStateAdmin | EngineStateUser

export type Entite = EntiteDTO & {
  identifiant: string
}

export const useHabilitationsAdminEngineStore = defineStore('habilitations-admin-v2', () => {
  const dataToken = ref<TokenData>()
  const engineState = shallowRef<EngineState>({ isRunning: false })

  function $reset() {
    // console.log('$reset')
    engineState.value = { isRunning: false }
    dataToken.value = undefined
  }

  const { setupCustomUpdater, setupFieldWithKey, setupFieldWithOptions } = useImmerFieldPatcher<EngineState>({
    getModel: () => engineState.value,
    setModel(model, _patch) {
      /* Gére le changement de token */
      if (model.jwtToken !== engineState.value.jwtToken) {
        if (model.jwtToken) {
          dataToken.value = extractDataFromToken(model.jwtToken)
        } else {
          dataToken.value = undefined
        }
      }

      if (model.type === 'administrateur' && engineState.value.type === "administrateur") {
        // Gére le changement de context en mode WebComponent
        if (model.context !== engineState.value.context && model.identifiant) {
          const forceRechargementIdentifiant = model.identifiant === engineState.value.identifiant

          if (forceRechargementIdentifiant) {
            const identifiant = model.identifiant
            nextTick(() => loadIdentifiantModel(identifiant))
          }
        }

        if (!deepEqual(model.identifiantModel, engineState.value.identifiantModel)) {
          let identifiant: string | undefined
          if (model.mode === 'contact') {
            const contact = model.identifiantModel as ContactDTO | undefined
            identifiant = contact?.email
          } else if (model.mode === 'entite') {
            const entite = model.identifiantModel as Entite | undefined
            identifiant = entite?.identifiant
          }

          model = {
            ...model,
            identifiantModel: model.identifiantModel,
            identifiant,
          }
        }
      } else if (model.type === 'utilisateur' && engineState.value.type === "utilisateur") {
        // Todo traiter le mode utilisateur
      }

      engineState.value = model
    },
  })

  async function initialise(props: HabilitationsAdminProps, isRunning?: boolean): Promise<void> {
    $reset()

    const preInit = setupCustomUpdater<HabilitationsAdminProps>((patch, props) => {
      patch.autoRun = props.autoRun
      patch.isRunning = isRunning ?? props.autoRun ?? false
      patch.context = props.context
      patch.jwtToken = props.jwtToken
      patch.type = props.type
      patch.enModeWebComponent = props.enModeWebComponent
      if (patch.type === 'administrateur' && props.type === 'administrateur') {
        patch.identifiantModel = undefined
        patch.contextReadOnly = props.contextReadOnly
        patch.identifiant = props.identifiant
        patch.itentifiantReadOnly = props.itentifiantReadOnly
        patch.identifiantHidden = props.identifiantHidden
        patch.mode = props.mode
        patch.modeReadOnly = props.modeReadOnly
      } else {
        delete (patch as any).contextReadOnly
        delete (patch as any).identifiant
        delete (patch as any).itentifiantReadOnly
        delete (patch as any).identifiantHidden
        delete (patch as any).mode
        delete (patch as any).modeReadOnly
      }
    })
    await preInit(props)
    if (engineState.value.type === 'administrateur') {
      await loadIdentifiantModel(engineState.value.identifiant)
    }
  }

  async function loadIdentifiantModel(identifiant: undefined | string) {
    // Teste si le chargement est possible
    if (!identifiant) return
    if (engineState.value.type !== 'administrateur') return
    if (!engineState.value.context) return
    if (!dataToken.value) return
    if (!engineState.value.isRunning) return
    if (!['entite', 'contact'].includes(engineState.value.mode ?? '')) return

    const entite = await (() =>
      engineState.value.mode === 'contact' ? getContact(identifiant) : getEntite(identifiant))()

    if (entite) {
      engineState.value = {
        ...engineState.value,
        identifiantModel: entite,
        identifiant: identifiant,
      }
    }
  }

  const identifiantInterneApi = computed(() => {
    if (engineState.value.type === 'utilisateur')
      return dataToken.value?.userId

    if (engineState.value.type === 'administrateur' && engineState.value.mode !== 'habilitations') {
      const identifiantModel = engineState.value.identifiantModel
      if (!identifiantModel) return

      return 'userId' in identifiantModel ? identifiantModel.userId : identifiantModel.identifiant
    }
    return undefined
  })

  const isReady = computed(() => {
    let result =
      dataToken.value &&
      engineState.value.context &&
      engineState.value.isRunning

    if (engineState.value.type === 'administrateur') {
      result = result && isNotNullUndefinedOrEmptyString(engineState.value.mode)
      if (engineState.value.mode !== 'habilitations') {
        result = result && isNotNullUndefinedOrEmptyString(identifiantInterneApi.value)
      }
    }

    return result
  })

  const estModeEntiteSirenSiret = computed(
    () => dataToken.value?.contextTypeUser === 'Interne' && dataToken?.value?.isImpersonating === 'False',
  )

  const isReadOnly = computed(() => {
    if (engineState.value.type !== 'administrateur') return true
    const isAdmin = dataToken.value?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'ADMIN'
    const forcedRO = engineState.value.contextReadOnly === true || engineState.value.itentifiantReadOnly === true
    const isImperso = dataToken.value?.isImpersonating === 'True'
    return forcedRO || isImperso || !isAdmin
  })

  function extractDataFromToken(token: string) {
    const rawData = urlSafeToStandard(token.split('.')[1]!)
    const json = b64DecodeUnicode(rawData)
    return JSON.parse(json) as TokenData
  }

  function getService() {
    const serviceReady =
      dataToken.value &&
      engineState.value.type === 'administrateur' &&
      engineState.value.context &&
      engineState.value.jwtToken &&
      ['entite', 'contact', 'habilitations'].includes(engineState.value.mode ?? '')

    if (!serviceReady) new Error("Le composant n'est pas prêt")

    return buildRattachementAdminService({
      baseUrl: dataToken.value!.aud,
      context: engineState.value.context!,
      token: engineState.value.jwtToken!,
      type: engineState.value.type!,
      mode: (engineState.value as any).mode,
    })
  }

  function searchContact(search: string, signal?: AbortSignal) {
    const service = getService()
    return service.searchContact(search, { dialogMode: 'silent', signal })
  }

  function searchEntite(search: string, signal?: AbortSignal) {
    const service = getService()
    return service.searchEntite(search, { dialogMode: 'silent', signal }).then((result) =>
      result.map(
        (entite) =>
          ({
            ...entite,
            identifiant: entite.nic ? `${entite.siren}${entite.nic}` : entite.siren,
          }) as Entite,
      ),
    )
  }

  function getContact(identifiant: string) {
    const service = getService()
    return service.getContact(identifiant)
  }
  function getEntite(identifiant: string) {
    const service = getService()
    return service.getEntite(identifiant).then((result) => {
      if (!result) return result
      return {
        ...result,
        identifiant: result.nic ? `${result.siren}${result.nic}` : result.siren,
      } as Entite
    })
  }

  const allowNavigate = computed(() => {
    if (engineState.value.type !== 'administrateur') return false
    const navigationInterdite =
      engineState.value.identifiantHidden === true ||
      engineState.value.itentifiantReadOnly === true
    return !navigationInterdite
  })

  const navigateTo = async (mode: HabilitationsAdminMode, identifiant?: string) => {
    if (engineState.value.type !== 'administrateur') return
    if (!allowNavigate.value) return
    const props = {
      ...engineState.value,
      identifiant,
      mode,
    } as HabilitationsAdminProps

    await initialise(props)
  }

  return {
    isReady,
    isReadOnly,
    dataToken,
    engineState,
    engineSetupCustomUpdater: setupCustomUpdater,
    engineSetupFieldWithKey: setupFieldWithKey,
    engineSetupFieldWithOptions: setupFieldWithOptions,
    estModeEntiteSirenSiret,
    getService,
    initialise,
    identifiantInterneApi,
    searchContact,
    searchEntite,
    allowNavigate,
    navigateTo,
  }
})
